/**
 * 606 MT5 Manual Trading — Orders Log page.
 *
 * Reads `GET /api/trading/mt5/manual/orders/log` and renders a chronological
 * feed of every multi-account dispatch (market / pending / cancel-pending)
 * with expandable per-account result rows.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, ScrollText, RefreshCw, Loader2, TrendingUp, TrendingDown, Target, Ban,
  CheckCircle2, XCircle, ChevronDown, ChevronUp, Filter, Bookmark,
} from 'lucide-react';

import { Input } from '../components/ui/input';
import DashboardHeader from '../components/DashboardHeader';
import PageBreadcrumb from '../components/PageBreadcrumb';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/trading/mt5/manual`;

function fmtDate(iso, isAr) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(isAr ? 'ar' : 'en', {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return iso;
  }
}

const KIND_META = {
  market:         { icon: TrendingUp, color: '#22c55e', ar: 'أمر فوري', en: 'Market' },
  pending:        { icon: Target,     color: '#38bdf8', ar: 'أمر معلّق', en: 'Pending' },
  cancel_pending: { icon: Ban,        color: '#ef4444', ar: 'إلغاء معلّق', en: 'Cancel Pending' },
};

export default function Meta5ManualTradingLog() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [filterKind, setFilterKind] = useState('all');
  const [filterSymbol, setFilterSymbol] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/orders/log?limit=200`, { headers: getAuthHeaders() });
      setOrders(res.data?.orders || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل التحميل' : 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let out = orders;
    if (filterKind !== 'all') out = out.filter((o) => o.kind === filterKind);
    if (filterSymbol.trim()) {
      const q = filterSymbol.trim().toUpperCase();
      out = out.filter((o) => (o.symbol || '').toUpperCase().includes(q));
    }
    return out;
  }, [orders, filterKind, filterSymbol]);

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white" data-testid="manual-log-page">
      <DashboardHeader
        showBackButton
        onBack={() => navigate('/meta5-trading/manual')}
      />

      <PageBreadcrumb items={[
        { label: '606 MT5 AI AND MANUAL TRADING', to: '/meta5-trading-hub' },
        { label: '606 MT5 MANUAL TRADING', to: '/meta5-trading/manual' },
        { label: isAr ? 'سجل الأوامر' : 'Orders Log' },
      ]} />

      {/* Action bar */}
      <div className="border-b border-white/[0.06] bg-black/40 backdrop-blur-md" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-500/15 border border-blue-500/30 flex items-center justify-center">
              <ScrollText className="h-4 w-4 text-blue-400" />
            </div>
            <div dir={isAr ? 'rtl' : 'ltr'}>
              <h2 className="text-base font-bold">{isAr ? 'سجل الأوامر' : 'Orders Log'}</h2>
              <p className="text-[11px] text-[#A1A1AA]">
                {isAr ? `${filtered.length} من ${orders.length} أمر` : `${filtered.length} of ${orders.length} orders`}
              </p>
            </div>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="p-2 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 disabled:opacity-50"
            data-testid="log-refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div dir={isAr ? 'rtl' : 'ltr'} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.02] p-3">
          <Filter className="h-4 w-4 text-white/40" />
          <div className="flex gap-1">
            {[
              { id: 'all', ar: 'الكل', en: 'All' },
              { id: 'market', ar: 'فوري', en: 'Market' },
              { id: 'pending', ar: 'معلّق', en: 'Pending' },
              { id: 'cancel_pending', ar: 'إلغاء', en: 'Cancel' },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilterKind(f.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${
                  filterKind === f.id
                    ? 'bg-[#d4af37]/20 text-[#d4af37] border-[#d4af37]/40'
                    : 'bg-white/[0.03] text-white/60 border-white/10 hover:text-white'
                }`}
                data-testid={`log-filter-${f.id}`}
              >
                {isAr ? f.ar : f.en}
              </button>
            ))}
          </div>
          <Input
            placeholder={isAr ? 'ابحث برمز... مثل XAUUSD' : 'Filter by symbol... e.g. XAUUSD'}
            value={filterSymbol}
            onChange={(e) => setFilterSymbol(e.target.value)}
            className="max-w-xs bg-black/40 border-white/10 text-white text-xs font-mono uppercase"
            data-testid="log-filter-symbol"
          />
        </div>

        {/* Feed */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-white/40">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <ScrollText className="h-12 w-12 text-white/20 mx-auto mb-3" />
            <p className="text-sm text-white/50">
              {isAr ? 'لا توجد أوامر مطابقة' : 'No matching orders'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((o, idx) => (
              <LogRow
                key={o.id || idx}
                order={o}
                isOpen={!!expanded[o.id || idx]}
                onToggle={() => toggle(o.id || idx)}
                isAr={isAr}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LogRow({ order, isOpen, onToggle, isAr }) {
  const meta = KIND_META[order.kind] || KIND_META.market;
  const Icon = meta.icon;
  const success = order.success_count || 0;
  const failure = order.failure_count || 0;
  const cancelled = order.total_cancelled || 0;

  return (
    <motion.div
      layout
      className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden"
      data-testid={`log-row-${order.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 hover:bg-white/[0.03] transition text-left"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}55` }}
        >
          <Icon className="h-4 w-4" style={{ color: meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold" style={{ color: meta.color }}>
              {isAr ? meta.ar : meta.en}
            </span>
            {order.symbol && (
              <span className="text-sm font-mono font-bold text-white">{order.symbol}</span>
            )}
            {order.side && (
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                order.side === 'buy' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-red-500/15 text-red-300'
              }`}>
                {order.side.toUpperCase()}
              </span>
            )}
            {order.order_type && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-500/15 text-blue-300 uppercase">
                {order.order_type.replace('_', ' ')}
              </span>
            )}
            {order.volume != null && (
              <span className="text-[11px] text-white/50">
                {isAr ? 'لوت:' : 'Lot:'} <span className="font-mono text-white/80">{order.volume}</span>
              </span>
            )}
            {order.template_name && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-[#d4af37]/15 text-[#d4af37] border border-[#d4af37]/30">
                <Bookmark className="h-2.5 w-2.5" />
                {order.template_name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-[11px] text-white/50">
            <span>{fmtDate(order.at, isAr)}</span>
            {order.by && <span>@{order.by}</span>}
            {order.reference_price != null && (
              <span>
                {isAr ? 'مرجعي:' : 'Ref:'} <span className="font-mono text-white/70">{Number(order.reference_price).toFixed(5)}</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {order.kind === 'cancel_pending' ? (
            <span className="text-xs font-bold text-red-300">
              {cancelled} {isAr ? 'ملغى' : 'cancelled'}
            </span>
          ) : (
            <>
              {success > 0 && (
                <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-bold">
                  <CheckCircle2 className="h-3.5 w-3.5" />{success}
                </span>
              )}
              {failure > 0 && (
                <span className="inline-flex items-center gap-1 text-red-400 text-xs font-bold">
                  <XCircle className="h-3.5 w-3.5" />{failure}
                </span>
              )}
            </>
          )}
          {isOpen ? <ChevronUp className="h-4 w-4 text-white/40" /> : <ChevronDown className="h-4 w-4 text-white/40" />}
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-white/10 bg-black/20"
          >
            <div className="p-3 space-y-1.5 text-xs">
              {order.entry_price != null && (
                <div className="text-white/60">
                  {isAr ? 'سعر الدخول:' : 'Entry price:'} <span className="font-mono text-white">{order.entry_price}</span>
                </div>
              )}
              {(order.sl != null || order.tp != null) && (
                <div className="flex gap-4 text-white/60">
                  {order.sl != null && <span>SL: <span className="font-mono text-red-300">{order.sl}</span></span>}
                  {order.tp != null && <span>TP: <span className="font-mono text-emerald-300">{order.tp}</span></span>}
                </div>
              )}
              {order.results?.length > 0 && (
                <div className="space-y-1 mt-2">
                  <div className="text-[10px] text-white/40 uppercase tracking-wider">
                    {isAr ? 'النتائج لكل حساب' : 'Per-account results'}
                  </div>
                  {order.results.map((r, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-between gap-3 px-2.5 py-1.5 rounded border ${
                        r.success
                          ? 'bg-emerald-500/[0.04] border-emerald-500/20'
                          : 'bg-red-500/[0.04] border-red-500/20'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {r.success
                          ? <CheckCircle2 className="h-3 w-3 text-emerald-400 shrink-0" />
                          : <XCircle className="h-3 w-3 text-red-400 shrink-0" />}
                        <span className="font-mono text-[10px] truncate">{r.account_id?.substring(0, 8)}</span>
                        {r.order_id && (
                          <span className="text-[10px] text-white/40 font-mono">#{r.order_id}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[10px] shrink-0">
                        {r.executed_price != null && (
                          <span className="text-emerald-300 font-mono">
                            @{Number(r.executed_price).toFixed(5)}
                          </span>
                        )}
                        {r.slippage != null && (
                          <span className={`font-mono ${
                            Math.abs(r.slippage) < 0.0001
                              ? 'text-emerald-300'
                              : Math.abs(r.slippage) < 0.001
                                ? 'text-amber-300'
                                : 'text-red-300'
                          }`}>
                            {r.slippage > 0 ? '+' : ''}{Number(r.slippage).toFixed(5)}
                          </span>
                        )}
                        {r.cancelled != null && r.cancelled > 0 && (
                          <span className="text-red-300 font-bold">{r.cancelled} ×</span>
                        )}
                        {!r.success && <span className="text-red-300 truncate max-w-[200px]">{r.error}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
